(async () => {
    const { Core } = await importModule('Core');

    // Shopify's native variant.available/inventory_quantity sum every location that tracks
    // an item, regardless of whether that location can actually fulfill online orders — a
    // second, non-fulfilling retail location with a negative stock count was making in-stock
    // items show as sold out. The Storefront API's storeAvailability is location-aware and,
    // since only the Malimali Warehouse location has local pickup enabled, it resolves to
    // exactly one (correct) entry per variant. This fetches that once per product page and
    // broadcasts it for other components to correct their displays with.
    const STOREFRONT_ACCESS_TOKEN = '19b4732a6f2e58d589d427322226667b';
    const STOREFRONT_API_PATH = '/api/2025-10/graphql.json';

    customElements.define('warehouse-stock', class extends Core {
        elements = {
            configJSON: '[data-warehouse-stock-config]'
        }

        async render() {
            const config = JSON.parse(this.$configJSON.textContent);
            if (!config.productId) return;
            try {
                const stock = await this._fetchStock(config.productId);
                // Inventory policy/management don't vary by location, so they're read
                // straight from Liquid rather than the Storefront API — merged in here so
                // consumers (e.g. the stock alert badge) can tell "genuinely sold out" apart
                // from "oversellable, should show preorder" once quantity hits 0.
                Object.keys(stock).forEach(variantId => {
                    const policy = config.variantPolicies?.[variantId];
                    if (policy) {
                        stock[variantId].managed = policy.managed;
                        stock[variantId].policy = policy.policy;
                    }
                });
                this.publish('warehouse-stock:ready', { stock });
            } catch (e) {
                console.error('[warehouse-stock] failed to fetch real-time stock:', e);
            }
        }

        async _fetchStock(productId) {
            const query = `{
                product(id: "${productId}") {
                    variants(first: 100) {
                        edges {
                            node {
                                id
                                storeAvailability(first: 1) {
                                    edges { node { available quantityAvailable } }
                                }
                            }
                        }
                    }
                }
            }`;

            const res = await fetch(STOREFRONT_API_PATH, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
                },
                body: JSON.stringify({ query })
            });
            const { data } = await res.json();

            const stock = {};
            (data?.product?.variants?.edges || []).forEach(({ node }) => {
                const variantId = node.id.split('/').pop();
                const entry = node.storeAvailability.edges[0]?.node;
                stock[variantId] = entry
                    // Clamp to 0 — a negative quantityAvailable happens when the warehouse
                    // itself is legitimately oversold (committed orders exceeding on-hand
                    // stock), not a cross-location bug. Nothing customer-facing should ever
                    // display a negative "left in stock" number.
                    ? { available: entry.available, quantity: Math.max(0, entry.quantityAvailable) }
                    : { available: false, quantity: 0 };
            });
            return stock;
        }
    });
})();
